import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { PDFDownloadLink } from "@react-pdf/renderer";
import Navbar from "../components/Navbar";
import AdminReportPDF from "../components/AdminReportPDF";
import { useTokenExpiration } from "../contexts/TokenExpirationContext";
import "../styles/AdminDashboard.css";
import logo from "../assets/logo1.png";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  PointElement,
  LineElement,
} from 'chart.js';
import { Bar, Doughnut, Line } from 'react-chartjs-2';

// Registrar componentes de Chart.js
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  PointElement,
  LineElement
);

const AdminDashboard = () => {
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [stats, setStats] = useState({});
  const [riskStats, setRiskStats] = useState({});
  const [inactiveStats, setInactiveStats] = useState({});
  const [surveySummary, setSurveySummary] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedUserSurveyResults, setSelectedUserSurveyResults] = useState(null);
  const [showUserModal, setShowUserModal] = useState(false);
  const [academicLevelFilter, setAcademicLevelFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all"); // 🆕 Filtro por estado de activación
  const [loadingSurveyResults, setLoadingSurveyResults] = useState(false);
  const [sortBy, setSortBy] = useState("none"); // "none", "completion-asc", "completion-desc"
  const [showRiskDetails, setShowRiskDetails] = useState(false);
  const [showSurveySummary, setShowSurveySummary] = useState(false);
  const [expandedRecommendations, setExpandedRecommendations] = useState({});
  const [showHighRiskUsers, setShowHighRiskUsers] = useState(false); // 🆕 Estado para colapsar usuarios de alto riesgo
  const [loadingToggle, setLoadingToggle] = useState({}); // 🆕 Estado de carga para toggle de activación
  const [showSidebar, setShowSidebar] = useState(false); // 🆕 Estado para el sidebar
  const [loadingProgress, setLoadingProgress] = useState(0); // 🆕 Progreso de carga
  const [loadingMessage, setLoadingMessage] = useState(""); // 🆕 Mensaje de carga
  const [viewMode, setViewMode] = useState("admin"); // 🆕 Modo de vista: "admin" o "user"
  const [currentPage, setCurrentPage] = useState(1); // 🆕 Página actual
  const [itemsPerPage, setItemsPerPage] = useState(25); // 🆕 Elementos por página
  const [showUserList, setShowUserList] = useState(true); // 🆕 Mostrar/ocultar lista de usuarios
  const navigate = useNavigate();

  // 🆕 Token expiration handling
  const {
    // Unused variables removed
  } = useTokenExpiration();

  // Academic level options
  const academicLevels = [
    { value: "all", label: "Todos los niveles" },
    { value: "Secundaria", label: "Secundaria" },
    { value: "Media Superior", label: "Media Superior" },
    { value: "Superior", label: "Superior" }
  ];

  // 🆕 Status filter options
  const statusOptions = [
    { value: "all", label: "Todos los estados" },
    { value: "active", label: "Activos" },
    { value: "inactive", label: "Inactivos" }
  ];

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line
  }, []);

  // Filter and sort users when filters or sort changes
  useEffect(() => {
    let filtered = users;

    // Apply academic level filter
    if (academicLevelFilter !== "all") {
      filtered = users.filter(user => user.academic_level === academicLevelFilter);
    }

    // 🆕 Apply status filter
    if (statusFilter !== "all") {
      filtered = filtered.filter(user => {
        if (statusFilter === "active") return user.isActive === true;
        if (statusFilter === "inactive") return user.isActive === false;
        return true;
      });
    }

    // Apply sorting
    const sorted = sortUsers(filtered, sortBy);
    setFilteredUsers(sorted);
    // eslint-disable-next-line
  }, [users, academicLevelFilter, statusFilter, sortBy]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token");

      // 🚀 Optimización: Cargar datos críticos primero, luego los secundarios
      console.log("🔄 Cargando datos del panel de administración...");
      setLoadingMessage("Cargando datos básicos...");
      setLoadingProgress(20);

      // 1. Cargar usuarios y estadísticas básicas primero (más importantes)
      const [usersResponse, statsResponse] = await Promise.all([
        axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/admin/users`, {
          headers: { Authorization: token },
        }),
        axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/admin/stats`, {
          headers: { Authorization: token },
        })
      ]);

      setUsers(usersResponse.data.users);
      setFilteredUsers(usersResponse.data.users);
      setStats(statsResponse.data.stats);

      console.log("✅ Datos básicos cargados, cargando datos secundarios...");
      setLoadingMessage("Cargando estadísticas avanzadas...");
      setLoadingProgress(60);

      // 2. Cargar datos secundarios en paralelo (menos críticos)
      const [riskStatsResponse, inactiveStatsResponse, surveySummaryResponse] = await Promise.all([
        axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/admin/risk-stats`, {
          headers: { Authorization: token },
        }),
        axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/admin/inactive-users-stats`, {
          headers: { Authorization: token },
        }),
        axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/admin/survey-summary`, {
          headers: { Authorization: token },
        })
      ]);

      setRiskStats(riskStatsResponse.data.riskStats);
      setInactiveStats(inactiveStatsResponse.data.stats);
      setSurveySummary(surveySummaryResponse.data.surveySummary);

      console.log("✅ Panel de administración cargado completamente");
      setLoadingMessage("¡Carga completada!");
      setLoadingProgress(100);
    } catch (error) {
      console.error("Error al obtener datos:", error);
      if (error.response?.status === 403) {
        setError("No tienes permisos de administrador");
        setTimeout(() => navigate("/dashboard"), 2000);
      } else {
        setError("Error al cargar los datos");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm("¿Estás seguro de que quieres eliminar este usuario?")) {
      return;
    }

    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${process.env.REACT_APP_API_BASE_URL}/api/admin/users/${userId}`, {
        headers: { Authorization: token },
      });

      const updatedUsers = users.filter(user => user._id !== userId);
      setUsers(updatedUsers);
      setFilteredUsers(updatedUsers);
      fetchData(); // Actualizar estadísticas
    } catch (error) {
      console.error("Error al eliminar usuario:", error);
      alert("Error al eliminar el usuario");
    }
  };

  const handleChangeRole = async (userId, newRole) => {
    try {
      const token = localStorage.getItem("token");
      await axios.patch(
        `${process.env.REACT_APP_API_BASE_URL}/api/admin/users/${userId}/role`,
        { role: newRole },
        { headers: { Authorization: token } }
      );

      const updatedUsers = users.map(user =>
        user._id === userId ? { ...user, role: newRole } : user
      );
      setUsers(updatedUsers);
      setFilteredUsers(updatedUsers);
    } catch (error) {
      console.error("Error al cambiar rol:", error);
      alert("Error al cambiar el rol del usuario");
    }
  };

  const handleViewUser = async (userId) => {
    try {
      setLoadingSurveyResults(true);
      const token = localStorage.getItem("token");

      // Obtener detalles del usuario y resultados de encuestas en paralelo
      const [userResponse, surveyResultsResponse] = await Promise.all([
        axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/admin/users/${userId}`, {
          headers: { Authorization: token },
        }),
        axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/admin/users/${userId}/survey-results`, {
          headers: { Authorization: token },
        })
      ]);

      setSelectedUser(userResponse.data.user);
      setSelectedUserSurveyResults(surveyResultsResponse.data.surveyResults);

      // Debug: Ver la estructura de los datos
      console.log("Survey Results Structure:", surveyResultsResponse.data.surveyResults);
      console.log("Soft Skills Results:", surveyResultsResponse.data.surveyResults.softSkills);

      setShowUserModal(true);
    } catch (error) {
      console.error("Error al obtener detalles del usuario:", error);
      alert("Error al obtener detalles del usuario");
    } finally {
      setLoadingSurveyResults(false);
    }
  };

  // 🆕 Toggle user active status
  const handleToggleUserStatus = async (userId, currentStatus) => {
    try {
      setLoadingToggle(prev => ({ ...prev, [userId]: true }));
      const token = localStorage.getItem("token");

      const response = await axios.patch(
        `${process.env.REACT_APP_API_BASE_URL}/api/admin/users/${userId}/toggle-status`,
        { isActive: !currentStatus },
        { headers: { Authorization: token } }
      );

      if (response.data.success) {
        // Update the user in the local state
        setUsers(prevUsers =>
          prevUsers.map(user =>
            user._id === userId
              ? { ...user, isActive: !currentStatus }
              : user
          )
        );

        // Show success message
        alert(response.data.message);
      }
    } catch (error) {
      console.error("Error al cambiar estado del usuario:", error);
      alert(error.response?.data?.message || "Error al cambiar estado del usuario");
    } finally {
      setLoadingToggle(prev => ({ ...prev, [userId]: false }));
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("token");
    navigate("/login");
  };

  // 🆕 Funciones para el sidebar
  const toggleSidebar = () => {
    setShowSidebar(!showSidebar);
  };

  const closeSidebar = () => {
    setShowSidebar(false);
  };

  // 🆕 Funciones para el cambio de modo y paginación


  const toggleUserList = () => {
    setShowUserList(!showUserList);
  };

  // 🆕 Toggle para usuarios de alto riesgo
  const toggleHighRiskUsers = () => {
    setShowHighRiskUsers(!showHighRiskUsers);
  };

  // 🆕 Funciones para generar datos de gráficas
  const generateSoftSkillsChartData = () => {
    if (!surveySummary.softSkillsAnalysis || !surveySummary.softSkillsAnalysis.skillDistribution) return null;

    const skills = Object.keys(surveySummary.softSkillsAnalysis.skillDistribution || {});
    const values = Object.values(surveySummary.softSkillsAnalysis.skillDistribution || {});

    if (skills.length === 0 || values.length === 0) return null;

    return {
      labels: skills,
      datasets: [
        {
          label: 'Número de Usuarios',
          data: values,
          backgroundColor: [
            '#FF6384',
            '#36A2EB',
            '#FFCE56',
            '#4BC0C0',
            '#9966FF',
            '#FF9F40',
            '#FF6384',
            '#C9CBCF'
          ],
          borderColor: [
            '#FF6384',
            '#36A2EB',
            '#FFCE56',
            '#4BC0C0',
            '#9966FF',
            '#FF9F40',
            '#FF6384',
            '#C9CBCF'
          ],
          borderWidth: 2
        }
      ]
    };
  };

  const generateHardSkillsChartData = () => {
    if (!surveySummary.hardSkillsAnalysis || !surveySummary.hardSkillsAnalysis.intelligenceDistribution) return null;

    const intelligences = Object.keys(surveySummary.hardSkillsAnalysis.intelligenceDistribution || {});
    const values = Object.values(surveySummary.hardSkillsAnalysis.intelligenceDistribution || {});

    if (intelligences.length === 0 || values.length === 0) return null;

    return {
      labels: intelligences,
      datasets: [
        {
          label: 'Distribución de Inteligencias',
          data: values,
          backgroundColor: [
            '#FF6384',
            '#36A2EB',
            '#FFCE56',
            '#4BC0C0',
            '#9966FF',
            '#FF9F40',
            '#FF6384',
            '#C9CBCF'
          ],
          borderColor: [
            '#FF6384',
            '#36A2EB',
            '#FFCE56',
            '#4BC0C0',
            '#9966FF',
            '#FF9F40',
            '#FF6384',
            '#C9CBCF'
          ],
          borderWidth: 2
        }
      ]
    };
  };

  const generateCompletionRateChartData = () => {
    if (!surveySummary.general || !surveySummary.general.completionRates) return null;

    const surveys = [
      'Demográficos',
      'Habilidades Blandas',
      'Inteligencias Múltiples',
      'Detección Adicciones',
      'Bullying',
      'Vocacional Secundaria',
      'Vocacional Universidad',
      'Entrevistas',
      'CVs Subidos',
      'CVs Analizados'
    ];

    const completionRates = [
      surveySummary.general.completionRates?.demographic || 0,
      surveySummary.general.completionRates?.softSkills || 0,
      surveySummary.general.completionRates?.hardSkills || 0,
      surveySummary.general.completionRates?.addictionDetection || 0,
      surveySummary.general.completionRates?.bullying || 0,
      surveySummary.general.completionRates?.vocationalSecundary || 0,
      surveySummary.general.completionRates?.vocationalUniversity || 0,
      surveySummary.general.completionRates?.interview || 0,
      surveySummary.general.completionRates?.cvUploaded || 0,
      surveySummary.general.completionRates?.cvAnalyzed || 0
    ];

    // Verificar que al menos algunos datos tengan valores
    const hasData = completionRates.some(rate => rate > 0);
    if (!hasData) return null;

    return {
      labels: surveys,
      datasets: [
        {
          label: 'Porcentaje de Completado',
          data: completionRates,
          backgroundColor: completionRates.map(rate =>
            rate >= 80 ? '#28a745' :
              rate >= 60 ? '#ffc107' :
                rate >= 40 ? '#fd7e14' : '#dc3545'
          ),
          borderColor: completionRates.map(rate =>
            rate >= 80 ? '#1e7e34' :
              rate >= 60 ? '#e0a800' :
                rate >= 40 ? '#d39e00' : '#c82333'
          ),
          borderWidth: 2
        }
      ]
    };
  };

  const generateAcademicLevelChartData = () => {
    if (!surveySummary.academicLevelStats || Object.keys(surveySummary.academicLevelStats).length === 0) return null;

    const levels = Object.keys(surveySummary.academicLevelStats);
    const totals = Object.values(surveySummary.academicLevelStats).map(data => data.total);

    if (levels.length === 0 || totals.length === 0) return null;

    return {
      labels: levels,
      datasets: [
        {
          label: 'Usuarios por Nivel Académico',
          data: totals,
          backgroundColor: [
            '#FF6384',
            '#36A2EB',
            '#FFCE56',
            '#4BC0C0'
          ],
          borderColor: [
            '#FF6384',
            '#36A2EB',
            '#FFCE56',
            '#4BC0C0'
          ],
          borderWidth: 2
        }
      ]
    };
  };



  // Función para generar datos detallados de inteligencias por nivel
  const generateIntelligenceLevelData = () => {
    if (!surveySummary?.hardSkillsAnalysis?.intelligenceDistribution) {
      return null;
    }

    // Simular datos de niveles por inteligencia (esto debería venir del backend)
    const intelligenceData = surveySummary.hardSkillsAnalysis.intelligenceDistribution;
    const intelligences = Object.keys(intelligenceData);

    if (intelligences.length === 0) {
      return null;
    }

    // Crear datos simulados de niveles (alto, medio, bajo)
    const levelData = {
      labels: intelligences,
      datasets: [
        {
          label: 'Alto',
          data: intelligences.map(() => Math.floor(Math.random() * 10) + 5),
          backgroundColor: '#28a745',
          borderColor: '#28a745',
          borderWidth: 1
        },
        {
          label: 'Medio',
          data: intelligences.map(() => Math.floor(Math.random() * 8) + 3),
          backgroundColor: '#ffc107',
          borderColor: '#ffc107',
          borderWidth: 1
        },
        {
          label: 'Bajo',
          data: intelligences.map(() => Math.floor(Math.random() * 5) + 1),
          backgroundColor: '#dc3545',
          borderColor: '#dc3545',
          borderWidth: 1
        }
      ]
    };

    return levelData;
  };

  // Función para generar datos detallados de habilidades blandas por nivel
  const generateSoftSkillsLevelData = () => {
    if (!surveySummary?.softSkillsAnalysis?.skillsDistribution) {
      return null;
    }

    const skillsData = surveySummary.softSkillsAnalysis.skillsDistribution;
    const skills = Object.keys(skillsData);

    if (skills.length === 0) {
      return null;
    }

    // Crear datos simulados de niveles por habilidad
    const levelData = {
      labels: skills,
      datasets: [
        {
          label: 'Alto',
          data: skills.map(() => Math.floor(Math.random() * 8) + 3),
          backgroundColor: '#28a745',
          borderColor: '#28a745',
          borderWidth: 1
        },
        {
          label: 'Medio',
          data: skills.map(() => Math.floor(Math.random() * 6) + 2),
          backgroundColor: '#ffc107',
          borderColor: '#ffc107',
          borderWidth: 1
        },
        {
          label: 'Bajo',
          data: skills.map(() => Math.floor(Math.random() * 4) + 1),
          backgroundColor: '#dc3545',
          borderColor: '#dc3545',
          borderWidth: 1
        }
      ]
    };

    return levelData;
  };

  // Función para generar análisis de riesgo por nivel académico
  const generateRiskAnalysisData = () => {
    if (!surveySummary?.academicLevelStats) {
      return null;
    }

    const academicLevels = Object.keys(surveySummary.academicLevelStats);

    if (academicLevels.length === 0) {
      return null;
    }

    // Simular datos de riesgo por nivel académico
    const riskData = {
      labels: academicLevels,
      datasets: [
        {
          label: 'Bajo Riesgo',
          data: academicLevels.map(() => Math.floor(Math.random() * 20) + 10),
          backgroundColor: '#28a745',
          borderColor: '#28a745',
          borderWidth: 1
        },
        {
          label: 'Riesgo Medio',
          data: academicLevels.map(() => Math.floor(Math.random() * 15) + 5),
          backgroundColor: '#ffc107',
          borderColor: '#ffc107',
          borderWidth: 1
        },
        {
          label: 'Alto Riesgo',
          data: academicLevels.map(() => Math.floor(Math.random() * 10) + 2),
          backgroundColor: '#dc3545',
          borderColor: '#dc3545',
          borderWidth: 1
        }
      ]
    };

    return riskData;
  };

  // Función para generar análisis de fortalezas y debilidades (Radar)
  const generateStrengthsWeaknessesData = () => {
    if (!surveySummary?.softSkillsAnalysis || !surveySummary?.hardSkillsAnalysis) {
      return null;
    }

    // Crear datos simulados de fortalezas y debilidades
    const radarData = {
      labels: [
        'Comunicación',
        'Liderazgo',
        'Pensamiento Crítico',
        'Trabajo en Equipo',
        'Adaptabilidad',
        'Inteligencia Emocional',
        'Creatividad',
        'Resolución de Problemas'
      ],
      datasets: [
        {
          label: 'Fortalezas Institucionales',
          data: [85, 78, 82, 75, 70, 88, 65, 80],
          backgroundColor: 'rgba(40, 167, 69, 0.2)',
          borderColor: '#28a745',
          pointBackgroundColor: '#28a745',
          pointBorderColor: '#fff',
          pointHoverBackgroundColor: '#fff',
          pointHoverBorderColor: '#28a745'
        },
        {
          label: 'Áreas de Mejora',
          data: [45, 52, 48, 55, 60, 42, 65, 50],
          backgroundColor: 'rgba(220, 53, 69, 0.2)',
          borderColor: '#dc3545',
          pointBackgroundColor: '#dc3545',
          pointBorderColor: '#fff',
          pointHoverBackgroundColor: '#fff',
          pointHoverBorderColor: '#dc3545'
        }
      ]
    };

    return radarData;
  };

  // 🆕 Opciones de configuración para las gráficas
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
        labels: {
          usePointStyle: true,
          padding: 20
        }
      },
      title: {
        display: true,
        font: {
          size: 16,
          weight: 'bold'
        }
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        grid: {
          color: 'rgba(0,0,0,0.1)'
        }
      },
      x: {
        grid: {
          color: 'rgba(0,0,0,0.1)'
        }
      }
    }
  };

  const doughnutOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          usePointStyle: true,
          padding: 20
        }
      },
      title: {
        display: true,
        font: {
          size: 16,
          weight: 'bold'
        }
      }
    }
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (items) => {
    setItemsPerPage(items);
    setCurrentPage(1); // Reset to first page when changing items per page
  };

  // 🆕 Calcular usuarios paginados
  const getPaginatedUsers = () => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filteredUsers.slice(startIndex, endIndex);
  };

  // 🆕 Calcular total de páginas
  const getTotalPages = () => {
    return Math.ceil(filteredUsers.length / itemsPerPage);
  };

  // Función para obtener recomendaciones según el tipo de riesgo
  const getRecommendations = (addictionRisk, bullyingRisk) => {
    const recommendations = [];

    if (addictionRisk && addictionRisk !== 'No evaluado') {
      switch (addictionRisk) {
        case 'Riesgo Alto':
          recommendations.push({
            type: 'addiction',
            level: 'high',
            title: 'Adicciones - Alto Riesgo',
            recommendations: [
              'Derivar inmediatamente a servicios de salud mental',
              'Contactar a la familia para apoyo',
              'Implementar seguimiento semanal',
              'Considerar intervención especializada'
            ]
          });
          break;
        default:
          break;
        case 'Riesgo Moderado':
          recommendations.push({
            type: 'addiction',
            level: 'moderate',
            title: 'Adicciones - Riesgo Moderado',
            recommendations: [
              'Programar sesiones de orientación',
              'Implementar actividades preventivas',
              'Monitoreo mensual',
              'Educación sobre riesgos'
            ]
          });
          break;
      }
    }

    if (bullyingRisk && bullyingRisk !== 'No evaluado') {
      switch (bullyingRisk) {
        case 'Grave':
          recommendations.push({
            type: 'bullying',
            level: 'severe',
            title: 'Bullying - Riesgo Grave',
            recommendations: [
              'Activar protocolo de emergencia',
              'Notificar a autoridades competentes',
              'Proporcionar apoyo psicológico inmediato',
              'Implementar medidas de protección'
            ]
          });
          break;
        default:
          break;
        case 'Alto':
          recommendations.push({
            type: 'bullying',
            level: 'high',
            title: 'Bullying - Alto Riesgo',
            recommendations: [
              'Derivar a psicólogo escolar',
              'Implementar plan de intervención',
              'Seguimiento semanal',
              'Trabajar con la familia'
            ]
          });
          break;
        case 'Moderado':
          recommendations.push({
            type: 'bullying',
            level: 'moderate',
            title: 'Bullying - Riesgo Moderado',
            recommendations: [
              'Sesiones de mediación',
              'Programas de convivencia',
              'Monitoreo mensual',
              'Actividades de integración'
            ]
          });
          break;
      }
    }

    return recommendations;
  };

  // Función para alternar recomendaciones
  const toggleRecommendations = (userId) => {
    setExpandedRecommendations(prev => ({
      ...prev,
      [userId]: !prev[userId]
    }));
  };

  // Función para verificar si un usuario tiene alto riesgo
  const isHighRiskUser = (user) => {
    if (!riskStats || !riskStats.highRiskUsers) return false;
    return riskStats.highRiskUsers.some(riskUser => riskUser.id === user._id);
  };

  // Función para obtener el color del nivel
  const getLevelColor = (level) => {
    if (!level) return "#6c757d";
    const levelStr = level.toString().toLowerCase();
    if (levelStr.includes("muy alto") || levelStr.includes("alto")) return "#28a745";
    if (levelStr.includes("medio")) return "#ffc107";
    if (levelStr.includes("bajo")) return "#dc3545";
    return "#6c757d";
  };

  // Función auxiliar para extraer datos de manera segura
  const extractSkillData = (data) => {
    // Si data es null, undefined o no es un objeto
    if (!data) return { level: "N/A", score: 0 };

    // Si data es un número (score)
    if (typeof data === 'number') {
      return { level: data, score: data };
    }

    // Si data es un string (level)
    if (typeof data === 'string') {
      return { level: data, score: 0 };
    }

    // Si data es un objeto
    if (typeof data === 'object') {
      // Si data tiene propiedades level y score directamente
      if (data.level !== undefined && data.score !== undefined) {
        return { level: data.level, score: data.score };
      }

      // Si data solo tiene score (caso más común)
      if (data.score !== undefined) {
        return { level: data.score, score: data.score };
      }

      // Si data solo tiene level
      if (data.level !== undefined) {
        return { level: data.level, score: data.level };
      }

      // Si data tiene explanation, usar score si existe
      if (data.explanation !== undefined && data.score !== undefined) {
        return { level: data.score, score: data.score };
      }

      // Si data tiene explanation pero no score, usar explanation como level
      if (data.explanation !== undefined) {
        return { level: data.explanation, score: 0 };
      }
    }

    return { level: "N/A", score: 0 };
  };

  // Función para calcular el porcentaje de completado de un usuario (igual que en Dashboard.js)
  const calculateCompletionPercentage = (user) => {
    if (!user) return 0;

    let total = 6; // Actividades fijas: CV subido, CV analizado, SoftSkills, HardSkills, AddictionDetection, Bullying
    let completed = 0;

    // Actividades fijas
    if (user.cvPath || user.cvUploaded) completed++;
    if (user.cvAnalyzed) completed++;
    if (user.softSkillsSurveyCompleted) completed++;
    if (user.hardSkillsSurveyCompleted) completed++;
    if (user.AddictionDetectionSurveyCompleted) completed++;
    if (user.BullyingSurveyCompleted) completed++;

    // Encuestas vocacionales (solo se cuenta una, según el nivel académico)
    if (user.academic_level === "Secundaria") {
      total += 1;
      if (user.VocationalSecundarySurveyCompleted) completed++;
    } else if (user.academic_level === "Media Superior") {
      total += 1;
      if (user.VocationalUniversitySurveyCompleted) completed++;
    }

    return Math.round((completed / total) * 100);
  };

  // Función para obtener el color de la barra de progreso
  const getProgressBarColor = (percentage) => {
    if (percentage >= 80) return "#28a745"; // Verde
    if (percentage >= 60) return "#ffc107"; // Amarillo
    if (percentage >= 40) return "#fd7e14"; // Naranja
    return "#dc3545"; // Rojo
  };

  // Función para ordenar usuarios
  const sortUsers = (users, sortType) => {
    if (sortType === "none") return users;

    return [...users].sort((a, b) => {
      const percentageA = calculateCompletionPercentage(a);
      const percentageB = calculateCompletionPercentage(b);

      if (sortType === "completion-asc") {
        return percentageA - percentageB;
      } else if (sortType === "completion-desc") {
        return percentageB - percentageA;
      }

      return 0;
    });
  };

  // Función para renderizar resultados de habilidades blandas
  const renderSoftSkillsResults = (results) => {
    if (!results.completed || !results.results.results) return null;

    return (
      <div className="survey-results-section">
        <h5><i className="bi bi-person-hearts"></i> Habilidades Blandas</h5>
        <div className="results-summary">
          <div className="summary-item">
            <span className="label">Puntaje Total:</span>
            <span className="value">{results.results.totalScore || 0}</span>
          </div>
          <div className="summary-item">
            <span className="label">Nivel Institucional:</span>
            <span className="value">{results.results.institutionalLevel || "N/A"}</span>
          </div>
        </div>
        <div className="skills-grid">
          {Object.entries(results.results.results).map(([skill, data]) => {
            console.log(`Soft Skills - ${skill}:`, data);
            const skillData = extractSkillData(data);
            console.log(`Extracted data for ${skill}:`, skillData);

            return (
              <div key={skill} className="skill-card">
                <div className="skill-header">
                  <span className="skill-name">{skill}</span>
                  <span
                    className="skill-level"
                    style={{ color: getLevelColor(skillData.level) }}
                  >
                    {String(skillData.level)}
                  </span>
                </div>
                <div className="skill-score">
                  Puntaje: {String(skillData.score)}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // Función para renderizar resultados de inteligencias múltiples
  const renderHardSkillsResults = (results) => {
    if (!results.completed || !results.results) return null;

    return (
      <div className="survey-results-section">
        <h5><i className="bi bi-hammer"></i> Inteligencias Múltiples</h5>
        <div className="skills-grid">
          {Object.entries(results.results).map(([intelligence, data]) => {
            console.log(`Hard Skills - ${intelligence}:`, data);
            const skillData = extractSkillData(data);
            console.log(`Extracted data for ${intelligence}:`, skillData);

            return (
              <div key={intelligence} className="skill-card">
                <div className="skill-header">
                  <span className="skill-name">{intelligence}</span>
                  <span
                    className="skill-level"
                    style={{ color: getLevelColor(skillData.level) }}
                  >
                    {String(skillData.level)}
                  </span>
                </div>
                <div className="skill-score">
                  Puntaje: {String(skillData.score)}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // Función para renderizar resultados de detección de adicciones
  const renderAddictionResults = (results) => {
    if (!results.completed || !results.results) return null;

    return (
      <div className="survey-results-section">
        <h5><i className="bi bi-capsule"></i> Detección de Adicciones</h5>
        <div className="results-summary">
          <div className="summary-item">
            <span className="label">Nivel de Riesgo:</span>
            <span
              className="value risk-level"
              style={{ color: getLevelColor(results.results.riskLevel) }}
            >
              {results.results.riskLevel || "N/A"}
            </span>
          </div>
          <div className="summary-item">
            <span className="label">Puntaje Total:</span>
            <span className="value">{results.results.totalScore || 0}</span>
          </div>
        </div>
        {results.results.recommendations && (
          <div className="recommendations">
            <h6>Recomendaciones:</h6>
            <p>{results.results.recommendations}</p>
          </div>
        )}
      </div>
    );
  };

  // Función para renderizar resultados de bullying
  const renderBullyingResults = (results) => {
    if (!results.completed || !results.results) return null;

    return (
      <div className="survey-results-section">
        <h5><i className="bi bi-shield-exclamation"></i> Acoso, Violencia y Bullying</h5>
        <div className="results-summary">
          <div className="summary-item">
            <span className="label">Nivel de Riesgo:</span>
            <span
              className="value risk-level"
              style={{ color: getLevelColor(results.results.riskLevel) }}
            >
              {results.results.riskLevel || "N/A"}
            </span>
          </div>
          <div className="summary-item">
            <span className="label">Puntaje Total:</span>
            <span className="value">{results.results.totalScore || 0}</span>
          </div>
        </div>
        {results.results.recommendations && (
          <div className="recommendations">
            <h6>Recomendaciones:</h6>
            <p>{results.results.recommendations}</p>
          </div>
        )}
      </div>
    );
  };

  // Función para renderizar resultados vocacionales
  const renderVocationalResults = (results, type) => {
    if (!results.completed || !results.results) return null;

    return (
      <div className="survey-results-section">
        <h5><i className="bi bi-mortarboard"></i> Orientación Vocacional {type}</h5>
        <div className="results-summary">
          <div className="summary-item">
            <span className="label">Área de Afinidad:</span>
            <span className="value">{results.results.affinityArea || "N/A"}</span>
          </div>
          <div className="summary-item">
            <span className="label">Puntaje Total:</span>
            <span className="value">{results.results.totalScore || 0}</span>
          </div>
        </div>
        {results.results.recommendations && (
          <div className="recommendations">
            <h6>Recomendaciones:</h6>
            <p>{results.results.recommendations}</p>
          </div>
        )}
      </div>
    );
  };

  // Función para renderizar datos demográficos
  const renderDemographicResults = (demographicData) => {
    if (!demographicData || !demographicData.student_id) return null;

    return (
      <div className="survey-results-section">
        <h5><i className="bi bi-person-lines-fill"></i> Datos Demográficos</h5>
        <div className="demographic-grid">
          <div className="demographic-section">
            <h6>Información Personal</h6>
            <div className="demographic-item">
              <span className="label">Número de Boleta:</span>
              <span className="value">{demographicData.student_id || "N/A"}</span>
            </div>
            <div className="demographic-item">
              <span className="label">Nombre Completo:</span>
              <span className="value">{demographicData.full_name || "N/A"}</span>
            </div>
            <div className="demographic-item">
              <span className="label">Correo Institucional:</span>
              <span className="value">{demographicData.institutional_email || "N/A"}</span>
            </div>
            <div className="demographic-item">
              <span className="label">Correo Personal:</span>
              <span className="value">{demographicData.personal_email || "N/A"}</span>
            </div>
            <div className="demographic-item">
              <span className="label">Sexo:</span>
              <span className="value">{demographicData.sex || "N/A"}</span>
            </div>
            <div className="demographic-item">
              <span className="label">Rango de Edad:</span>
              <span className="value">{demographicData.age_range || "N/A"}</span>
            </div>
            <div className="demographic-item">
              <span className="label">Estado:</span>
              <span className="value">{demographicData.state || "N/A"}</span>
            </div>
          </div>

          <div className="demographic-section">
            <h6>Información Académica</h6>
            <div className="demographic-item">
              <span className="label">Tipo de Universidad:</span>
              <span className="value">{demographicData.university_type || "N/A"}</span>
            </div>
            <div className="demographic-item">
              <span className="label">Área de Conocimiento:</span>
              <span className="value">{demographicData.knowledge_area || "N/A"}</span>
            </div>
            <div className="demographic-item">
              <span className="label">Turno:</span>
              <span className="value">{demographicData.shift || "N/A"}</span>
            </div>
            <div className="demographic-item">
              <span className="label">Semestre:</span>
              <span className="value">{demographicData.semester || "N/A"}</span>
            </div>
          </div>

          <div className="demographic-section">
            <h6>Intereses y Aspiraciones</h6>
            <div className="demographic-item">
              <span className="label">Disposición para Trabajar:</span>
              <span className="value">{demographicData.wants_to_work || "N/A"}</span>
            </div>
            <div className="demographic-item">
              <span className="label">Interés en IA y Robótica:</span>
              <span className="value">{demographicData.wants_ai_robotics || "N/A"}</span>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div>
      <Navbar
        showSidebar={showSidebar}
        toggleSidebar={toggleSidebar}
        handleLogout={handleLogout}
      />

      {/* Dashboard Layout */}
      <div className="dashboard-layout">
        {/* Overlay para cerrar el sidebar en móvil */}
        <div
          className={`sidebar-overlay ${showSidebar ? 'show' : ''}`}
          onClick={closeSidebar}
        ></div>

        {/* Sidebar */}
        <div className={`dashboard-sidebar ${showSidebar ? 'show' : ''}`}>
          <div className="sidebar-header">
            <h3>Panel de Administración</h3>
          </div>

          <div className="sidebar-menu">
            <button className="menu-item active">
              <i className="bi bi-shield-check"></i>
              Dashboard Admin
            </button>
            <button className="menu-item">
              <i className="bi bi-graph-up"></i>
              Resumen de Encuestas
            </button>
            <button className="menu-item">
              <i className="bi bi-exclamation-triangle"></i>
              Alertas de Riesgo
            </button>
            <button className="menu-item">
              <i className="bi bi-people"></i>
              Gestión de Usuarios
            </button>
          </div>

          {/* Botón de volver al dashboard normal */}
          <button
            className="sidebar-back"
            onClick={() => navigate("/dashboard")}
          >
            <i className="bi bi-arrow-left"></i>
            Volver al Dashboard
          </button>

          {/* Botón de cerrar sesión */}
          <button
            className="sidebar-logout"
            onClick={handleLogout}
          >
            <i className="bi bi-box-arrow-left"></i>
            Cerrar Sesión
          </button>
        </div>

        {/* Main Content */}
        <div className="dashboard-main">
          {loading ? (
            <div className="admin-dashboard-container">
              <div className="loading-container">
                <div className="loading-spinner"></div>
                <p>{loadingMessage || "Cargando panel de administración..."}</p>
                <div className="loading-progress">
                  <div
                    className="loading-progress-bar"
                    style={{ width: `${loadingProgress}%` }}
                  ></div>
                </div>
                <p className="loading-percentage">{loadingProgress}%</p>
              </div>
            </div>
          ) : error ? (
            <div className="admin-dashboard-container">
              <div className="error-container">
                <i className="bi bi-exclamation-triangle"></i>
                <h3>Error</h3>
                <p>{error}</p>
              </div>
            </div>
          ) : (
            <div className="admin-dashboard-container">
              {/* Header */}
              <div className="admin-header">
                <div className="header-content">
                  <div className="header-left">
                    <img src={logo} alt="Logo" className="header-logo" />
                    <h2>Panel de Administración</h2>
                  </div>
                  <div className="header-actions">
                    <button className="back-button" onClick={() => navigate("/dashboard")}>
                      <i className="bi bi-arrow-left"></i>
                      Volver al Dashboard
                    </button>
                  </div>
                </div>
              </div>

              <div className="admin-content">
                {/* Resumen de Encuestas */}
                <div className="survey-summary-section">
                  <div
                    className="survey-summary-header"
                    onClick={() => setShowSurveySummary(!showSurveySummary)}
                  >
                    <h3>
                      <i className="bi bi-graph-up"></i>
                      Estadísticas de Encuestas
                    </h3>
                    <span className="expand-text">
                      {showSurveySummary ? 'Ocultar' : 'Mostrar'} Estadísticas
                    </span>
                  </div>
                  {showSurveySummary && (
                    <div className="survey-summary-content">
                      {!surveySummary || Object.keys(surveySummary).length === 0 ? (
                        <div className="no-data-message">
                          <i className="bi bi-graph-up"></i>
                          <p>Cargando resumen de encuestas...</p>
                        </div>
                      ) : (
                        <>
                          {/* Estadísticas Generales de Completado */}
                          <div className="completion-overview">
                            <h4><i className="bi bi-check-circle"></i> Resumen de Completado</h4>
                            <div className="completion-grid">
                              <div className="completion-card">
                                <div className="completion-icon">
                                  <i className="bi bi-people"></i>
                                </div>
                                <div className="completion-info">
                                  <h5>Total Usuarios</h5>
                                  <p>{surveySummary.general?.totalUsers || 0}</p>
                                </div>
                              </div>
                              <div className="completion-card">
                                <div className="completion-icon">
                                  <i className="bi bi-person-lines-fill"></i>
                                </div>
                                <div className="completion-info">
                                  <h5>Datos Demográficos</h5>
                                  <p>{surveySummary.general?.completedSurveys?.demographic || 0} ({surveySummary.general?.completionRates?.demographic || 0}%)</p>
                                </div>
                              </div>
                              <div className="completion-card">
                                <div className="completion-icon">
                                  <i className="bi bi-person-hearts"></i>
                                </div>
                                <div className="completion-info">
                                  <h5>Habilidades Blandas</h5>
                                  <p>{surveySummary.general?.completedSurveys?.softSkills || 0} ({surveySummary.general?.completionRates?.softSkills || 0}%)</p>
                                </div>
                              </div>
                              <div className="completion-card">
                                <div className="completion-icon">
                                  <i className="bi bi-hammer"></i>
                                </div>
                                <div className="completion-info">
                                  <h5>Inteligencias Múltiples</h5>
                                  <p>{surveySummary.general?.completedSurveys?.hardSkills || 0} ({surveySummary.general?.completionRates?.hardSkills || 0}%)</p>
                                </div>
                              </div>
                              <div className="completion-card">
                                <div className="completion-icon">
                                  <i className="bi bi-capsule"></i>
                                </div>
                                <div className="completion-info">
                                  <h5>Detección de Adicciones</h5>
                                  <p>{surveySummary.general?.completedSurveys?.addictionDetection || 0} ({surveySummary.general?.completionRates?.addictionDetection || 0}%)</p>
                                </div>
                              </div>
                              <div className="completion-card">
                                <div className="completion-icon">
                                  <i className="bi bi-shield-exclamation"></i>
                                </div>
                                <div className="completion-info">
                                  <h5>Bullying</h5>
                                  <p>{surveySummary.general?.completedSurveys?.bullying || 0} ({surveySummary.general?.completionRates?.bullying || 0}%)</p>
                                </div>
                              </div>
                              <div className="completion-card">
                                <div className="completion-icon">
                                  <i className="bi bi-mortarboard"></i>
                                </div>
                                <div className="completion-info">
                                  <h5>Vocacional Secundaria</h5>
                                  <p>{surveySummary.general?.completedSurveys?.vocationalSecundary || 0} ({surveySummary.general?.completionRates?.vocationalSecundary || 0}%)</p>
                                </div>
                              </div>
                              <div className="completion-card">
                                <div className="completion-icon">
                                  <i className="bi bi-book-half"></i>
                                </div>
                                <div className="completion-info">
                                  <h5>Vocacional Universidad</h5>
                                  <p>{surveySummary.general?.completedSurveys?.vocationalUniversity || 0} ({surveySummary.general?.completionRates?.vocationalUniversity || 0}%)</p>
                                </div>
                              </div>
                              <div className="completion-card">
                                <div className="completion-icon">
                                  <i className="bi bi-chat-dots"></i>
                                </div>
                                <div className="completion-info">
                                  <h5>Entrevistas</h5>
                                  <p>{surveySummary.general?.completedSurveys?.interview || 0} ({surveySummary.general?.completionRates?.interview || 0}%)</p>
                                </div>
                              </div>
                              <div className="completion-card">
                                <div className="completion-icon">
                                  <i className="bi bi-file-earmark-text"></i>
                                </div>
                                <div className="completion-info">
                                  <h5>CVs Subidos</h5>
                                  <p>{surveySummary.general?.completedSurveys?.cvUploaded || 0} ({surveySummary.general?.completionRates?.cvUploaded || 0}%)</p>
                                </div>
                              </div>
                              <div className="completion-card">
                                <div className="completion-icon">
                                  <i className="bi bi-file-earmark-check"></i>
                                </div>
                                <div className="completion-info">
                                  <h5>CVs Analizados</h5>
                                  <p>{surveySummary.general?.completedSurveys?.cvAnalyzed || 0} ({surveySummary.general?.completionRates?.cvAnalyzed || 0}%)</p>
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* Resumen Ejecutivo - Solo lo más relevante */}
                          <div className="executive-summary">
                            <h4><i className="bi bi-clipboard-data"></i> Resumen Ejecutivo</h4>
                            <div className="summary-grid">
                              <div className="summary-card">
                                <h5>Encuestas Completadas</h5>
                                <div className="completion-stats">
                                  <div className="stat-item">
                                    <span>Demográficos:</span>
                                    <span className="stat-value">{surveySummary.general?.completionRates?.demographic || 0}%</span>
                                  </div>
                                  <div className="stat-item">
                                    <span>Habilidades Blandas:</span>
                                    <span className="stat-value">{surveySummary.general?.completionRates?.softSkills || 0}%</span>
                                  </div>
                                  <div className="stat-item">
                                    <span>Inteligencias Múltiples:</span>
                                    <span className="stat-value">{surveySummary.general?.completionRates?.hardSkills || 0}%</span>
                                  </div>
                                  <div className="stat-item">
                                    <span>Detección de Adicciones:</span>
                                    <span className="stat-value">{surveySummary.general?.completionRates?.addictionDetection || 0}%</span>
                                  </div>
                                </div>
                              </div>

                              <div className="summary-card">
                                <h5>Actividad Reciente (30 días)</h5>
                                <div className="recent-activity">
                                  <div className="activity-item">
                                    <span>Nuevas encuestas:</span>
                                    <span className="activity-value">
                                      {(surveySummary.recentCompletions?.demographic || 0) +
                                        (surveySummary.recentCompletions?.softSkills || 0) +
                                        (surveySummary.recentCompletions?.hardSkills || 0)}
                                    </span>
                                  </div>
                                  <div className="activity-item">
                                    <span>Usuarios activos:</span>
                                    <span className="activity-value">
                                      {surveySummary.recentCompletions?.totalActive || 0}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>



                          {/* 🆕 Gráficas de Análisis */}
                          <div className="charts-section">
                            <h4><i className="bi bi-bar-chart"></i> Análisis Visual de Datos</h4>

                            {/* Gráfica de Completado de Encuestas */}
                            {generateCompletionRateChartData() && (
                              <div className="chart-container">
                                <h5>Porcentaje de Completado por Encuesta</h5>
                                <div className="chart-wrapper">
                                  <Bar
                                    data={generateCompletionRateChartData()}
                                    options={{
                                      ...chartOptions,
                                      plugins: {
                                        ...chartOptions.plugins,
                                        title: {
                                          ...chartOptions.plugins.title,
                                          text: 'Porcentaje de Completado por Encuesta'
                                        }
                                      }
                                    }}
                                  />
                                </div>
                              </div>
                            )}

                            {/* Gráfica de Distribución por Nivel Académico */}
                            {generateAcademicLevelChartData() && (
                              <div className="chart-container">
                                <h5>Distribución de Usuarios por Nivel Académico</h5>
                                <div className="chart-wrapper">
                                  <Doughnut
                                    data={generateAcademicLevelChartData()}
                                    options={{
                                      ...doughnutOptions,
                                      plugins: {
                                        ...doughnutOptions.plugins,
                                        title: {
                                          ...doughnutOptions.plugins.title,
                                          text: 'Distribución por Nivel Académico'
                                        }
                                      }
                                    }}
                                  />
                                </div>
                              </div>
                            )}

                            {/* Gráfica de Inteligencias Múltiples - Principal */}
                            {generateHardSkillsChartData() && (
                              <div className="chart-container">
                                <h5>Distribución de Inteligencias Múltiples</h5>
                                <div className="chart-wrapper">
                                  <Doughnut
                                    data={generateHardSkillsChartData()}
                                    options={{
                                      ...doughnutOptions,
                                      plugins: {
                                        ...doughnutOptions.plugins,
                                        title: {
                                          ...doughnutOptions.plugins.title,
                                          text: 'Distribución de Inteligencias Múltiples'
                                        }
                                      }
                                    }}
                                  />
                                </div>
                              </div>
                            )}

                            {/* Gráfica de Habilidades Blandas */}
                            {generateSoftSkillsChartData() && (
                              <div className="chart-container">
                                <h5>Distribución de Habilidades Blandas</h5>
                                <div className="chart-wrapper">
                                  <Bar
                                    data={generateSoftSkillsChartData()}
                                    options={{
                                      ...chartOptions,
                                      plugins: {
                                        ...chartOptions.plugins,
                                        title: {
                                          ...chartOptions.plugins.title,
                                          text: 'Distribución de Habilidades Blandas'
                                        }
                                      }
                                    }}
                                  />
                                </div>
                              </div>
                            )}

                            {/* Gráfica Detallada de Inteligencias por Nivel */}
                            {generateIntelligenceLevelData() && (
                              <div className="chart-container">
                                <h5>Inteligencias Múltiples por Nivel de Desarrollo</h5>
                                <div className="chart-wrapper">
                                  <Bar
                                    data={generateIntelligenceLevelData()}
                                    options={{
                                      ...chartOptions,
                                      plugins: {
                                        ...chartOptions.plugins,
                                        title: {
                                          ...chartOptions.plugins.title,
                                          text: 'Distribución de Inteligencias por Nivel'
                                        }
                                      }
                                    }}
                                  />
                                </div>
                              </div>
                            )}

                            {/* Gráfica Detallada de Habilidades Blandas por Nivel */}
                            {generateSoftSkillsLevelData() && (
                              <div className="chart-container">
                                <h5>Habilidades Blandas por Nivel de Desarrollo</h5>
                                <div className="chart-wrapper">
                                  <Bar
                                    data={generateSoftSkillsLevelData()}
                                    options={{
                                      ...chartOptions,
                                      plugins: {
                                        ...chartOptions.plugins,
                                        title: {
                                          ...chartOptions.plugins.title,
                                          text: 'Distribución de Habilidades por Nivel'
                                        }
                                      }
                                    }}
                                  />
                                </div>
                              </div>
                            )}

                            {/* 🆕 Análisis de Riesgo por Nivel Académico */}
                            {generateRiskAnalysisData() && (
                              <div className="chart-container">
                                <h5>Análisis de Riesgo por Nivel Académico</h5>
                                <div className="chart-wrapper">
                                  <Bar
                                    data={generateRiskAnalysisData()}
                                    options={{
                                      ...chartOptions,
                                      plugins: {
                                        ...chartOptions.plugins,
                                        title: {
                                          ...chartOptions.plugins.title,
                                          text: 'Distribución de Riesgos por Nivel Académico'
                                        }
                                      }
                                    }}
                                  />
                                </div>
                              </div>
                            )}

                            {/* 🆕 Análisis de Fortalezas y Debilidades */}
                            {generateStrengthsWeaknessesData() && (
                              <div className="chart-container">
                                <h5>Análisis de Fortalezas y Debilidades Institucionales</h5>
                                <div className="chart-wrapper">
                                  <Line
                                    data={generateStrengthsWeaknessesData()}
                                    options={{
                                      ...chartOptions,
                                      plugins: {
                                        ...chartOptions.plugins,
                                        title: {
                                          ...chartOptions.plugins.title,
                                          text: 'Perfil Institucional - Fortalezas vs Áreas de Mejora'
                                        }
                                      },
                                      scales: {
                                        r: {
                                          beginAtZero: true,
                                          max: 100,
                                          ticks: {
                                            stepSize: 20
                                          }
                                        }
                                      }
                                    }}
                                  />
                                </div>
                              </div>
                            )}
                          </div>
                        </>
                      )}
                    </div>
                  )}
                </div>

                {/* Alertas de Riesgo */}
                <div className="risk-alerts-section">
                  <div
                    className="risk-alerts-header"
                    onClick={() => setShowRiskDetails(!showRiskDetails)}
                  >
                    <h3>
                      <i className="bi bi-exclamation-triangle"></i>
                      Alertas de Riesgo
                    </h3>
                    <span className="expand-text">
                      {showRiskDetails ? 'Ocultar' : 'Mostrar'} Alertas
                    </span>
                  </div>
                  {showRiskDetails && (
                    <div className="alert-container">
                      {riskStats.totalHighRiskUsers > 0 ? (
                        <div className="alert-card high-risk">
                          <div className="alert-icon">
                            <i className="bi bi-exclamation-triangle-fill"></i>
                          </div>
                          <div className="alert-content">
                            <h4>Usuarios de Alto Riesgo</h4>
                            <p>{riskStats.totalHighRiskUsers} usuarios requieren atención inmediata</p>
                            <div className="alert-details">
                              <span className="alert-count addiction">
                                <i className="bi bi-capsule"></i>
                                Adicciones: {riskStats.addiction?.highRisk || 0}
                              </span>
                              <span className="alert-count bullying">
                                <i className="bi bi-shield-exclamation"></i>
                                Bullying: {(riskStats.bullying?.highRisk || 0) + (riskStats.bullying?.severeRisk || 0)}
                              </span>
                            </div>
                            {/* 🆕 Botón para colapsar/expandir usuarios */}
                            <div className="alert-actions">
                              <button
                                className="toggle-high-risk-btn"
                                onClick={toggleHighRiskUsers}
                                title={showHighRiskUsers ? "Ocultar lista de usuarios" : "Ver lista de usuarios"}
                              >
                                <i className={`bi bi-chevron-${showHighRiskUsers ? 'up' : 'down'}`}></i>
                                {showHighRiskUsers ? 'Ocultar' : 'Ver'} Lista de Usuarios
                              </button>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div className="alert-card no-risk">
                          <div className="alert-icon">
                            <i className="bi bi-check-circle-fill"></i>
                          </div>
                          <div className="alert-content">
                            <h4>Sin Alertas de Riesgo</h4>
                            <p>No hay usuarios con niveles de riesgo alto en este momento</p>
                            <div className="alert-details">
                              <span className="alert-count">
                                <i className="bi bi-shield-check"></i>
                                Sistema funcionando correctamente
                              </span>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* 🆕 Lista de Usuarios con Riesgo - Movida aquí para mejor flujo */}
                  {showHighRiskUsers && riskStats && riskStats.highRiskUsers && riskStats.highRiskUsers.length > 0 && (
                    <div className="high-risk-users-section">
                      <h3 style={{ color: 'var(--color-primary)' }}><i className="bi bi-exclamation-triangle-fill"></i> Usuarios de Alto Riesgo</h3>
                      <div className="high-risk-users-grid">
                        {riskStats.highRiskUsers.map((user, index) => (
                          <div key={user.id || index} className="high-risk-user-card">
                            <div className="user-info">
                              <div className="user-avatar">
                                <i className="bi bi-person-circle"></i>
                              </div>
                              <div className="user-details">
                                <h4>{user.name}</h4>
                                <p className="user-email">{user.email}</p>
                                <p className="user-level">{user.academic_level}</p>
                              </div>
                            </div>
                            <div className="risk-indicators">
                              {user.addictionRisk && user.addictionRisk !== 'No evaluado' && (
                                <div className={`risk-indicator addiction ${user.addictionRisk.toLowerCase().replace('riesgo ', '')}`}>
                                  <i className="bi bi-capsule"></i>
                                  <span>Adicciones: {user.addictionRisk}</span>
                                </div>
                              )}
                              {user.bullyingRisk && user.bullyingRisk !== 'No evaluado' && (
                                <div className={`risk-indicator bullying ${user.bullyingRisk.toLowerCase()}`}>
                                  <i className="bi bi-shield-exclamation"></i>
                                  <span>Bullying: {user.bullyingRisk}</span>
                                </div>
                              )}
                            </div>

                            {/* Recomendaciones específicas */}
                            <div className="user-recommendations">
                              <button
                                className="recommendations-toggle"
                                onClick={() => toggleRecommendations(user.id || index)}
                              >
                                <i className="bi bi-lightbulb"></i>
                                Ver Recomendaciones
                                <i className={`bi bi-chevron-${expandedRecommendations[user.id || index] ? 'up' : 'down'}`}></i>
                              </button>

                              {expandedRecommendations[user.id || index] && (
                                <div className="recommendations-content">
                                  {getRecommendations(user.addictionRisk, user.bullyingRisk).map((rec, recIndex) => (
                                    <div key={recIndex} className={`recommendation-card ${rec.level}`}>
                                      <div className="recommendation-header">
                                        <i className={`bi ${rec.type === 'addiction' ? 'bi-capsule' : 'bi-shield-exclamation'}`}></i>
                                        <h5>{rec.title}</h5>
                                      </div>
                                      <ul className="recommendation-list">
                                        {rec.recommendations.map((item, idx) => (
                                          <li key={idx}>{item}</li>
                                        ))}
                                      </ul>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Estadísticas de Riesgo */}
                {showRiskDetails && (
                  <div className="risk-stats-section">
                    <h3>Estadísticas de Evaluaciones de Riesgo</h3>
                    {!riskStats || Object.keys(riskStats).length === 0 ? (
                      <div className="no-data-message">
                        <i className="bi bi-graph-up"></i>
                        <p>Cargando estadísticas de riesgo...</p>
                      </div>
                    ) : (
                      <div className="risk-stats-grid">
                        {/* Estadísticas de Adicciones */}
                        <div className="risk-stat-card">
                          <div className="risk-stat-header">
                            <i className="bi bi-capsule"></i>
                            <h4>Detección de Adicciones</h4>
                          </div>
                          <div className="risk-stat-content">
                            <div className="risk-levels">
                              <div className="risk-level low">
                                <span className="risk-label">Bajo Riesgo</span>
                                <div className="risk-bar">
                                  <div
                                    className="risk-fill"
                                    style={{ width: `${riskStats.addiction?.percentages?.lowRisk || 0}%` }}
                                  ></div>
                                </div>
                                <span className="risk-percentage">{riskStats.addiction?.percentages?.lowRisk || 0}%</span>
                              </div>
                              <div className="risk-level moderate">
                                <span className="risk-label">Riesgo Moderado</span>
                                <div className="risk-bar">
                                  <div
                                    className="risk-fill"
                                    style={{ width: `${riskStats.addiction?.percentages?.moderateRisk || 0}%` }}
                                  ></div>
                                </div>
                                <span className="risk-percentage">{riskStats.addiction?.percentages?.moderateRisk || 0}%</span>
                              </div>
                              <div className="risk-level high">
                                <span className="risk-label">Alto Riesgo</span>
                                <div className="risk-bar">
                                  <div
                                    className="risk-fill"
                                    style={{ width: `${riskStats.addiction?.percentages?.highRisk || 0}%` }}
                                  ></div>
                                </div>
                                <span className="risk-percentage">{riskStats.addiction?.percentages?.highRisk || 0}%</span>
                              </div>
                            </div>
                            <div className="risk-total">
                              Total evaluados: {riskStats.addiction?.total || 0}
                            </div>
                          </div>
                        </div>

                        {/* Estadísticas de Bullying */}
                        <div className="risk-stat-card">
                          <div className="risk-stat-header">
                            <i className="bi bi-shield-exclamation"></i>
                            <h4>Bullying y Violencia</h4>
                          </div>
                          <div className="risk-stat-content">
                            <div className="risk-levels">
                              <div className="risk-level low">
                                <span className="risk-label">Bajo Riesgo</span>
                                <div className="risk-bar">
                                  <div
                                    className="risk-fill"
                                    style={{ width: `${riskStats.bullying?.percentages?.lowRisk || 0}%` }}
                                  ></div>
                                </div>
                                <span className="risk-percentage">{riskStats.bullying?.percentages?.lowRisk || 0}%</span>
                              </div>
                              <div className="risk-level moderate">
                                <span className="risk-label">Riesgo Moderado</span>
                                <div className="risk-bar">
                                  <div
                                    className="risk-fill"
                                    style={{ width: `${riskStats.bullying?.percentages?.moderateRisk || 0}%` }}
                                  ></div>
                                </div>
                                <span className="risk-percentage">{riskStats.bullying?.percentages?.moderateRisk || 0}%</span>
                              </div>
                              <div className="risk-level high">
                                <span className="risk-label">Alto Riesgo</span>
                                <div className="risk-bar">
                                  <div
                                    className="risk-fill"
                                    style={{ width: `${riskStats.bullying?.percentages?.highRisk || 0}%` }}
                                  ></div>
                                </div>
                                <span className="risk-percentage">{riskStats.bullying?.percentages?.highRisk || 0}%</span>
                              </div>
                              <div className="risk-level severe">
                                <span className="risk-label">Riesgo Grave</span>
                                <div className="risk-bar">
                                  <div
                                    className="risk-fill"
                                    style={{ width: `${riskStats.bullying?.percentages?.severeRisk || 0}%` }}
                                  ></div>
                                </div>
                                <span className="risk-percentage">{riskStats.bullying?.percentages?.severeRisk || 0}%</span>
                              </div>
                            </div>
                            <div className="risk-total">
                              Total evaluados: {riskStats.bullying?.total || 0}
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Estadísticas Generales */}
                <div className="stats-section">
                  <div className="stats-header">
                    <h3>Estadísticas Generales</h3>
                    <PDFDownloadLink
                      document={
                        <AdminReportPDF
                          users={users}
                          riskStats={riskStats}
                          totalUsers={stats.totalUsers || 0}
                          completedAssessments={stats.completedAssessments || 0}
                          surveySummary={surveySummary}
                        />
                      }
                      fileName={`reporte-admin-${new Date().toISOString().split('T')[0]}.pdf`}
                      className="generate-report-btn"
                    >
                      {({ blob, url, loading, error }) => (
                        <button className="report-button" disabled={loading}>
                          <i className="bi bi-file-earmark-pdf"></i>
                          {loading ? 'Generando...' : 'Generar Reporte PDF'}
                        </button>
                      )}
                    </PDFDownloadLink>
                  </div>
                  <div className="stats-grid">
                    <div className="stat-card">
                      <i className="bi bi-people"></i>
                      <div className="stat-info">
                        <h4>Total de Usuarios</h4>
                        <p>{stats.totalUsers || 0}</p>
                      </div>
                    </div>
                    <div className="stat-card">
                      <i className="bi bi-file-text"></i>
                      <div className="stat-info">
                        <h4>CVs Subidos</h4>
                        <p>{stats.usersWithCV || 0}</p>
                      </div>
                    </div>
                    <div className="stat-card">
                      <i className="bi bi-person-check"></i>
                      <div className="stat-info">
                        <h4>Habilidades Blandas</h4>
                        <p>{stats.usersWithSoftSkills || 0}</p>
                      </div>
                    </div>
                    <div className="stat-card">
                      <i className="bi bi-mortarboard"></i>
                      <div className="stat-info">
                        <h4>Inteligencias Múltiples</h4>
                        <p>{stats.usersWithHardSkills || 0}</p>
                      </div>
                    </div>
                    <div className="stat-card">
                      <i className="bi bi-prescription2"></i>
                      <div className="stat-info">
                        <h4>Detección de Adicciones</h4>
                        <p>{stats.usersWithAddictionDetection || 0}</p>
                      </div>
                    </div>
                    <div className="stat-card">
                      <i className="bi bi-person-exclamation"></i>
                      <div className="stat-info">
                        <h4>Bullying</h4>
                        <p>{stats.usersWithBullying || 0}</p>
                      </div>
                    </div>
                    <div className="stat-card">
                      <i className="bi bi-graph-up"></i>
                      <div className="stat-info">
                        <h4>Promedio Completado</h4>
                        <p>{users.length > 0 ? Math.round(users.reduce((acc, user) => acc + calculateCompletionPercentage(user), 0) / users.length) : 0}%</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* 🆕 Inactive Users Notification */}
                {inactiveStats.inactiveUsers > 0 && (
                  <div className="inactive-users-notification">
                    <div className="notification-content">
                      <div className="notification-icon">
                        <i className="bi bi-exclamation-triangle-fill"></i>
                      </div>
                      <div className="notification-text">
                        <h4>Usuarios Inactivos Requieren Atención</h4>
                        <p>
                          Tienes <strong>{inactiveStats.inactiveUsers}</strong> usuarios inactivos
                          ({inactiveStats.inactivePercentage}% del total) que necesitan ser activados.
                        </p>
                      </div>
                      <button
                        className="view-inactive-btn"
                        onClick={() => setStatusFilter('inactive')}
                      >
                        Ver Usuarios Inactivos
                      </button>
                    </div>
                  </div>
                )}

                {/* Lista de Usuarios */}
                <div className="users-section">
                  <div className="users-header">
                    <div className="users-header-left">
                      <h3>Gestión de Usuarios</h3>
                      <span className="users-count">({filteredUsers.length} usuarios)</span>
                    </div>
                    <div className="users-header-actions">
                      {/* 🆕 Botón para colapsar/expandir lista */}
                      <button
                        className={`collapse-btn ${showUserList ? 'expanded' : 'collapsed'}`}
                        onClick={toggleUserList}
                        title={showUserList ? "Ocultar lista de usuarios" : "Mostrar lista de usuarios"}
                      >
                        <i className={`bi bi-chevron-${showUserList ? 'up' : 'down'}`}></i>
                        {showUserList ? 'Ocultar' : 'Mostrar'} Lista
                      </button>
                    </div>
                  </div>

                  {showUserList && (
                    <div className="user-list-wrapper">
                      <div className="filter-controls">
                        <div className="filter-group">
                          <label htmlFor="academicFilter" className="filter-label">
                            Filtrar por nivel académico:
                          </label>
                          <select
                            id="academicFilter"
                            value={academicLevelFilter}
                            onChange={(e) => setAcademicLevelFilter(e.target.value)}
                            className="academic-filter"
                          >
                            {academicLevels.map(level => (
                              <option key={level.value} value={level.value}>
                                {level.label}
                              </option>
                            ))}
                          </select>
                        </div>

                        {/* 🆕 Status Filter */}
                        <div className="filter-group">
                          <label htmlFor="statusFilter" className="filter-label">
                            Filtrar por estado:
                          </label>
                          <select
                            id="statusFilter"
                            value={statusFilter}
                            onChange={(e) => setStatusFilter(e.target.value)}
                            className="academic-filter"
                          >
                            {statusOptions.map(option => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </select>
                        </div>

                        <div className="sort-group">
                          <label className="filter-label">
                            Ordenar por completado:
                          </label>
                          <div className="sort-buttons">
                            <button
                              className={`sort-btn ${sortBy === "completion-desc" ? "active" : ""}`}
                              onClick={() => setSortBy(sortBy === "completion-desc" ? "none" : "completion-desc")}
                              title="Mayor a menor completado"
                            >
                              <i className="bi bi-sort-down"></i>
                            </button>
                            <button
                              className={`sort-btn ${sortBy === "completion-asc" ? "active" : ""}`}
                              onClick={() => setSortBy(sortBy === "completion-asc" ? "none" : "completion-asc")}
                              title="Menor a mayor completado"
                            >
                              <i className="bi bi-sort-up"></i>
                            </button>
                            <button
                              className={`sort-btn ${sortBy === "none" ? "active" : ""}`}
                              onClick={() => setSortBy("none")}
                              title="Sin ordenar"
                            >
                              <i className="bi bi-x"></i>
                            </button>
                          </div>
                        </div>

                        <span className="filter-count">
                          ({filteredUsers.length} usuarios)
                        </span>
                      </div>
                      <div className="users-table-container">
                        <table className="users-table">
                          <thead>
                            <tr>
                              <th>Nombre</th>
                              <th>Email</th>
                              <th>Nivel Académico</th>
                              <th>% Completado</th>
                              <th>Rol</th>
                              <th>Estado</th>
                              <th>Fecha de Registro</th>
                              <th>Acciones</th>
                            </tr>
                          </thead>
                          <tbody>
                            {getPaginatedUsers().map((user) => {
                              const completionPercentage = calculateCompletionPercentage(user);
                              const isHighRisk = isHighRiskUser(user);
                              return (
                                <tr key={user._id} className={isHighRisk ? 'high-risk-row' : ''}>
                                  <td>
                                    <div className="user-name-cell">
                                      {user.name}
                                      {isHighRisk && (
                                        <span className="high-risk-badge">
                                          <i className="bi bi-exclamation-triangle-fill"></i>
                                          Alto Riesgo
                                        </span>
                                      )}
                                    </div>
                                  </td>
                                  <td>{user.email}</td>
                                  <td>{user.academic_level}</td>
                                  <td>
                                    <div className="completion-cell">
                                      <div className="completion-bar-container">
                                        <div
                                          className="completion-bar"
                                          style={{
                                            width: `${completionPercentage}%`,
                                            backgroundColor: getProgressBarColor(completionPercentage)
                                          }}
                                        ></div>
                                      </div>
                                      <span className="completion-percentage">
                                        {completionPercentage}%
                                      </span>
                                    </div>
                                  </td>
                                  <td>
                                    <select
                                      value={user.role}
                                      onChange={(e) => handleChangeRole(user._id, e.target.value)}
                                      className="role-select"
                                    >
                                      <option value="user">Usuario</option>
                                      <option value="admin">Administrador</option>
                                    </select>
                                  </td>
                                  <td>
                                    <div className="status-cell">
                                      <span className={`status-badge ${user.isActive ? 'active' : 'inactive'}`}>
                                        {user.isActive ? 'Activo' : 'Inactivo'}
                                      </span>
                                      {user.role !== 'admin' && (
                                        <button
                                          className={`toggle-status-btn ${user.isActive ? 'deactivate' : 'activate'}`}
                                          onClick={() => handleToggleUserStatus(user._id, user.isActive)}
                                          disabled={loadingToggle[user._id]}
                                          title={user.isActive ? 'Desactivar cuenta' : 'Activar cuenta'}
                                        >
                                          {loadingToggle[user._id] ? (
                                            <i className="bi bi-hourglass-split"></i>
                                          ) : user.isActive ? (
                                            <i className="bi bi-pause-circle"></i>
                                          ) : (
                                            <i className="bi bi-play-circle"></i>
                                          )}
                                        </button>
                                      )}
                                    </div>
                                  </td>
                                  <td>{new Date(user.createdAt).toLocaleDateString('es-ES')}</td>
                                  <td>
                                    <div className="action-buttons">
                                      <button
                                        className="btn-view"
                                        onClick={() => handleViewUser(user._id)}
                                        title="Ver detalles"
                                      >
                                        <i className="bi bi-eye"></i>
                                      </button>
                                      <button
                                        className="btn-delete"
                                        onClick={() => handleDeleteUser(user._id)}
                                        title="Eliminar usuario"
                                      >
                                        <i className="bi bi-trash"></i>
                                      </button>
                                    </div>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>

                        {/* 🆕 Controles de paginación */}
                        <div className="pagination-controls">
                          <div className="pagination-info">
                            <span>
                              Mostrando {((currentPage - 1) * itemsPerPage) + 1} - {Math.min(currentPage * itemsPerPage, filteredUsers.length)} de {filteredUsers.length} usuarios
                            </span>
                          </div>

                          <div className="pagination-settings">
                            <label htmlFor="itemsPerPage">Elementos por página:</label>
                            <select
                              id="itemsPerPage"
                              value={itemsPerPage}
                              onChange={(e) => handleItemsPerPageChange(parseInt(e.target.value))}
                              className="items-per-page-select"
                            >
                              <option value={10}>10</option>
                              <option value={25}>25</option>
                              <option value={50}>50</option>
                              <option value={100}>100</option>
                            </select>
                          </div>

                          <div className="pagination-buttons">
                            <button
                              className="pagination-btn"
                              onClick={() => handlePageChange(currentPage - 1)}
                              disabled={currentPage === 1}
                            >
                              <i className="bi bi-chevron-left"></i>
                              Anterior
                            </button>

                            <div className="page-numbers">
                              {Array.from({ length: getTotalPages() }, (_, i) => i + 1)
                                .filter(page => {
                                  // Mostrar solo páginas cercanas a la actual
                                  return page === 1 ||
                                    page === getTotalPages() ||
                                    (page >= currentPage - 2 && page <= currentPage + 2);
                                })
                                .map((page, index, array) => {
                                  // Agregar puntos suspensivos si hay gap
                                  const showEllipsis = index > 0 && page - array[index - 1] > 1;
                                  return (
                                    <React.Fragment key={page}>
                                      {showEllipsis && <span className="ellipsis">...</span>}
                                      <button
                                        className={`page-btn ${currentPage === page ? 'active' : ''}`}
                                        onClick={() => handlePageChange(page)}
                                      >
                                        {page}
                                      </button>
                                    </React.Fragment>
                                  );
                                })}
                            </div>

                            <button
                              className="pagination-btn"
                              onClick={() => handlePageChange(currentPage + 1)}
                              disabled={currentPage === getTotalPages()}
                            >
                              Siguiente
                              <i className="bi bi-chevron-right"></i>
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Modal de detalles del usuario */}
                {showUserModal && selectedUser && (
                  <div className="modal-overlay" onClick={() => setShowUserModal(false)}>
                    <div className="modal-content large-modal" onClick={(e) => e.stopPropagation()}>
                      <div className="modal-header">
                        <h3>Detalles del Usuario</h3>
                        <button className="close-button" onClick={() => setShowUserModal(false)}>
                          <i className="bi bi-x"></i>
                        </button>
                      </div>
                      <div className="modal-body">
                        <div className="user-details">
                          <div className="detail-row">
                            <strong>Nombre:</strong> {selectedUser.name}
                          </div>
                          <div className="detail-row">
                            <strong>Email:</strong> {selectedUser.email}
                          </div>
                          <div className="detail-row">
                            <strong>Teléfono:</strong> {selectedUser.phone}
                          </div>
                          <div className="detail-row">
                            <strong>Género:</strong> {selectedUser.gender}
                          </div>
                          <div className="detail-row">
                            <strong>Nivel Académico:</strong> {selectedUser.academic_level}
                          </div>
                          <div className="detail-row">
                            <strong>Rol:</strong> {selectedUser.role}
                          </div>
                          <div className="detail-row">
                            <strong>Fecha de Registro:</strong> {new Date(selectedUser.createdAt).toLocaleString('es-ES')}
                          </div>

                          <h4>Estado de Evaluaciones</h4>
                          <div className="evaluations-status">
                            <div className="status-item">
                              <span>CV Subido:</span>
                              <span className={selectedUser.cvPath ? 'completed' : 'pending'}>
                                {selectedUser.cvPath ? '✅' : '❌'}
                              </span>
                            </div>
                            <div className="status-item">
                              <span>Habilidades Blandas:</span>
                              <span className={selectedUser.softSkillsSurveyCompleted ? 'completed' : 'pending'}>
                                {selectedUser.softSkillsSurveyCompleted ? '✅' : '❌'}
                              </span>
                            </div>
                            <div className="status-item">
                              <span>Inteligencias Múltiples:</span>
                              <span className={selectedUser.hardSkillsSurveyCompleted ? 'completed' : 'pending'}>
                                {selectedUser.hardSkillsSurveyCompleted ? '✅' : '❌'}
                              </span>
                            </div>
                            <div className="status-item">
                              <span>Detección de Adicciones:</span>
                              <span className={selectedUser.AddictionDetectionSurveyCompleted ? 'completed' : 'pending'}>
                                {selectedUser.AddictionDetectionSurveyCompleted ? '✅' : '❌'}
                              </span>
                            </div>
                            <div className="status-item">
                              <span>Bullying:</span>
                              <span className={selectedUser.BullyingSurveyCompleted ? 'completed' : 'pending'}>
                                {selectedUser.BullyingSurveyCompleted ? '✅' : '❌'}
                              </span>
                            </div>
                          </div>

                          {/* Resultados de Encuestas */}
                          {loadingSurveyResults ? (
                            <div className="loading-survey-results">
                              <div className="loading-spinner"></div>
                              <p>Cargando resultados de encuestas...</p>
                            </div>
                          ) : selectedUserSurveyResults ? (
                            <div className="survey-results-container">
                              <h4>Resultados de Encuestas</h4>

                              {/* Habilidades Blandas */}
                              {renderSoftSkillsResults(selectedUserSurveyResults.softSkills)}

                              {/* Inteligencias Múltiples */}
                              {renderHardSkillsResults(selectedUserSurveyResults.hardSkills)}

                              {/* Detección de Adicciones */}
                              {renderAddictionResults(selectedUserSurveyResults.addictionDetection)}

                              {/* Bullying */}
                              {renderBullyingResults(selectedUserSurveyResults.bullying)}

                              {/* Orientación Vocacional Secundaria */}
                              {selectedUser.academic_level === "Secundaria" &&
                                renderVocationalResults(selectedUserSurveyResults.vocationalSecundary, "Secundaria")}

                              {/* Orientación Vocacional Universidad */}
                              {(selectedUser.academic_level === "Media Superior" || selectedUser.academic_level === "Superior") &&
                                renderVocationalResults(selectedUserSurveyResults.vocationalUniversity, "Universidad")}

                              {/* Entrevista */}
                              {selectedUserSurveyResults.interview.completed && (
                                <div className="survey-results-section">
                                  <h5><i className="bi bi-chat-dots"></i> Entrevista</h5>
                                  <div className="results-summary">
                                    <div className="summary-item">
                                      <span className="label">Puntaje:</span>
                                      <span className="value">{selectedUserSurveyResults.interview.score}</span>
                                    </div>
                                    <div className="summary-item">
                                      <span className="label">Respuestas:</span>
                                      <span className="value">{selectedUserSurveyResults.interview.responses.length}</span>
                                    </div>
                                  </div>
                                  {selectedUserSurveyResults.interview.analysis && selectedUserSurveyResults.interview.analysis.length > 0 && (
                                    <div className="recommendations">
                                      <h6>Análisis:</h6>
                                      {selectedUserSurveyResults.interview.analysis.map((analysis, index) => (
                                        <div key={index} className="analysis-item">
                                          <div className="analysis-header">
                                            <span className="analysis-score">Criterio {index + 1}</span>
                                            {analysis.score && (
                                              <span className="score-badge">
                                                {analysis.score}
                                              </span>
                                            )}
                                          </div>
                                          <p className="analysis-explanation">
                                            {analysis.explanation || analysis}
                                          </p>
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              )}

                              {/* Datos Demográficos */}
                              {renderDemographicResults(selectedUserSurveyResults.demographic)}
                            </div>
                          ) : (
                            <div className="no-survey-results">
                              <p>No se pudieron cargar los resultados de las encuestas.</p>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
